<template>
  <div class="json-node" :class="{ 'highlighted': isHighlighted }">
    <div class="json-line" v-if="!isRoot">
      <span class="json-toggle" @click="toggleCollapse" v-if="isExpandable">
        <b-icon :icon="collapsed ? 'chevron-right' : 'chevron-down'" font-scale="0.8"></b-icon>
      </span>
      <span class="json-toggle-placeholder" v-else></span>
      
      <span class="json-key">{{ keyName }}</span>
      <span class="json-separator">: </span>
      
      <span class="json-value" :class="`json-${valueType}`">
        <template v-if="isExpandable && collapsed">
          <span class="json-bracket">{{ isArray ? '[' : '{' }}</span>
          <span class="json-count"> {{ itemCount }} {{ isArray ? 'items' : 'keys' }} </span>
          <span class="json-bracket">{{ isArray ? ']' : '}' }}</span>
        </template>
        <template v-else-if="isExpandable && !collapsed">
          <span class="json-bracket">{{ isArray ? '[' : '{' }}</span>
        </template>
        <template v-else>
          <span v-if="valueType === 'string'" class="json-string">"{{ displayValue }}"</span>
          <span v-else-if="valueType === 'null'" class="json-null">null</span>
          <span v-else-if="valueType === 'boolean'" class="json-boolean">{{ displayValue }}</span>
          <span v-else-if="valueType === 'number'" class="json-number">{{ displayValue }}</span>
        </template>
      </span>

      <span class="json-actions" v-if="!readOnly && !isExpandable">
        <b-icon 
          icon="pencil" 
          class="json-action-icon" 
          @click="startEdit"
          v-b-tooltip.hover 
          title="Edit value"
        ></b-icon>
      </span>

      <span class="json-copy" @click="copyValue" v-b-tooltip.hover title="Copy value">
        <b-icon icon="clipboard" font-scale="0.8"></b-icon>
      </span>
    </div>

    <!-- Opening bracket for root node -->
    <div class="json-line" v-if="isRoot && isExpandable">
      <span class="json-bracket">{{ isArray ? '[' : '{' }}</span>
    </div>

    <div v-if="isExpandable && !collapsed" class="json-children">
      <JsonNode
        v-for="(value, key) in data"
        :key="key"
        :data="value"
        :keyName="key"
        :depth="depth + 1"
        :maxDepth="maxDepth"
        :searchQuery="searchQuery"
        :readOnly="readOnly"
        :path="[...path, key]"
        @update="$emit('update', $event.path, $event.value)"
        @first-match="$emit('first-match', $event)"
      />
      <div class="json-line" v-if="!isRoot">
        <span class="json-toggle-placeholder"></span>
        <span class="json-bracket">{{ isArray ? ']' : '}' }}</span>
      </div>
    </div>

    <!-- Closing bracket for root node (outside children so it aligns with opening bracket) -->
    <div class="json-line" v-if="isRoot && isExpandable && !collapsed">
      <span class="json-bracket">{{ isArray ? ']' : '}' }}</span>
    </div>

    <!-- Edit Modal -->
    <main-dialog v-model="editing" :title="`Edit: ${fullPath}`" @ok="saveEdit" @cancel="cancelEdit">
      <b-form-group label="Value">
        <b-form-input v-model="editValue" v-if="valueType === 'string' || valueType === 'number'"></b-form-input>
        <b-form-select v-model="editValue" :options="[true, false]" v-else-if="valueType === 'boolean'"></b-form-select>
      </b-form-group>
    </main-dialog>
  </div>
</template>

<script>
export default {
  name: 'JsonNode',
  props: {
    data: {
      type: [Object, Array, String, Number, Boolean],
      required: true,
      validator: (value) => {
        return value !== undefined
      }
    },
    keyName: {
      type: [String, Number],
      default: ''
    },
    depth: {
      type: Number,
      default: 0
    },
    maxDepth: {
      type: Number,
      default: 3
    },
    searchQuery: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    path: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isRoot() {
      return this.keyName === 'root' && this.depth === 0
    },
    isArray() {
      return Array.isArray(this.data)
    },
    isObject() {
      return this.data !== null && typeof this.data === 'object' && !Array.isArray(this.data)
    },
    isExpandable() {
      return this.isArray || this.isObject
    },
    valueType() {
      if (this.data === null) return 'null'
      if (Array.isArray(this.data)) return 'array'
      return typeof this.data
    },
    displayValue() {
      return this.data
    },
    itemCount() {
      if (this.isArray) return this.data.length
      if (this.isObject) return Object.keys(this.data).length
      return 0
    },
    fullPath() {
      return this.path.join('.')
    },
    isHighlighted() {
      if (!this.searchQuery) return false
      const query = this.searchQuery.toLowerCase()
      const keyMatch = String(this.keyName).toLowerCase().includes(query)
      const valueMatch = !this.isExpandable && String(this.data).toLowerCase().includes(query)
      return keyMatch || valueMatch
    }
  },
  data() {
    return {
      collapsed: this.depth >= this.maxDepth,
      editing: false,
      editValue: null,
      firstMatchEmitted: false
    }
  },
  watch: {
    searchQuery(newVal, oldVal) {
      if (newVal && this.isHighlighted && this.collapsed) {
        this.collapsed = false
      }
      
      // Reset first match flag when search changes
      if (newVal !== oldVal) {
        this.firstMatchEmitted = false
      }
    }
  },
  methods: {
    checkFirstMatch() {
      this.firstMatchEmitted = false
      
      if (this.isHighlighted && !this.firstMatchEmitted) {
        this.emitFirstMatch()
        return true
      }
      
      // Check children
      if (this.isExpandable && this.$children) {
        for (const child of this.$children) {
          if (child.checkFirstMatch && child.checkFirstMatch()) {
            return true
          }
        }
      }
      
      return false
    },

    emitFirstMatch() {
      if (!this.firstMatchEmitted) {
        this.firstMatchEmitted = true
        this.$nextTick(() => {
          const element = this.$el.querySelector('.json-line')
          if (element) {
            this.$emit('first-match', element)
          }
        })
      }
    },
    toggleCollapse() {
      this.collapsed = !this.collapsed
    },
    copyValue() {
      const value = this.isExpandable ? JSON.stringify(this.data, null, 2) : this.data
      navigator.clipboard.writeText(String(value))
      this.$bvToast.toast('Copied to clipboard', {
        variant: 'success',
        autoHideDelay: 2000,
        solid: true,
        toaster: 'b-toaster-bottom-right'
      })
    },
    startEdit() {
      this.editValue = this.data
      this.editing = true
    },
    saveEdit() {
      let finalValue = this.editValue
      
      // Type conversion
      if (this.valueType === 'number') {
        finalValue = Number(this.editValue)
      } else if (this.valueType === 'boolean') {
        finalValue = Boolean(this.editValue)
      }
      
      this.$emit('update', { path: this.path, value: finalValue })
    },
    cancelEdit() {
      this.editValue = null
    }
  }
}
</script>

<style scoped>
.json-node {
  position: relative;
}

.json-line {
  display: flex;
  align-items: center;
  padding: 2px 0;
  gap: 4px;
  transition: background-color 0.2s;
}

.json-line:hover {
  background-color: var(--bg-hover, rgba(0, 0, 0, 0.02));
}

.json-node.highlighted > .json-line {
  background-color: rgba(255, 235, 59, 0.3);
}

.json-toggle {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  width: 16px;
  color: var(--text-secondary, #6c757d);
  user-select: none;
}

.json-toggle:hover {
  color: var(--text-primary, #212529);
}

.json-toggle-placeholder {
  display: inline-block;
  width: 16px;
}

.json-key {
  color: #0066cc;
  font-weight: 600;
}

.json-separator {
  color: var(--text-secondary, #6c757d);
}

.json-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.json-string {
  color: #c41a16;
}

.json-number {
  color: #1c00cf;
}

.json-boolean {
  color: #0d47a1;
  font-weight: 600;
}

.json-null {
  color: #9e9e9e;
  font-style: italic;
}

.json-bracket {
  color: var(--text-secondary, #6c757d);
  font-weight: bold;
}

.json-count {
  color: var(--text-muted, #9e9e9e);
  font-size: 0.9em;
  font-style: italic;
}

.json-children {
  padding-left: 20px;
  border-left: 1px solid var(--border-separator, #e0e0e0);
  margin-left: 8px;
}

.json-actions,
.json-copy {
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  margin-left: 4px;
}

.json-line:hover .json-actions,
.json-line:hover .json-copy {
  opacity: 1;
}

.json-action-icon {
  color: var(--primary, #007bff);
  font-size: 0.85em;
}

.json-action-icon:hover {
  color: var(--primary-dark, #0056b3);
}

.json-copy {
  color: var(--text-secondary, #6c757d);
  font-size: 0.85em;
}

.json-copy:hover {
  color: var(--text-primary, #212529);
}
</style>
